/**
 * "Yesterday's shift assignment is still pending" — on the screen, not just in
 * the bell.
 *
 * The daily cron notifies roster owners once, at day start. That is not enough
 * on its own: a bell item that was dismissed, or arrived while somebody was
 * off, leaves no trace of a day that will finalise as absent. This card puts the
 * same finding in front of whoever opens the Roster screen.
 *
 * Two findings, two fixes, so they are shown separately rather than merged into
 * one number: employees with nothing governing the day need assigning, whereas
 * an unpublished roster just needs publishing.
 *
 * Renders nothing at all when there is nothing to report, when the feature is
 * off, or when the request fails — this sits above an existing screen and must
 * never be the reason that screen looks broken.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, CalendarX2, FileWarning } from "lucide-react";
import dayjs from "dayjs";

import { Button } from "../../components/ui/button";
import { PENDING_SHIFT_ALERTS } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";

const NAMES_SHOWN = 6;

const PendingShiftAlert = () => {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();
    const [report, setReport] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    const load = useCallback(async (signal) => {
        // No setLoading and no toast on failure: the alert is additive, so a
        // slow or 403'd request should leave the Roster screen exactly as it
        // would have been without it.
        const response = await fetch(PENDING_SHIFT_ALERTS, {}, null, signal);
        if (response?.status === "cancelled") return;
        if (response?.status !== "success") return;
        setReport(response.data || null);
    }, [fetch]);

    useEffect(() => {
        const controller = new AbortController();
        load(controller.signal);
        return () => controller.abort();
    }, [load]);

    if (dismissed || !report?.feature_enabled || !report?.has_findings) return null;

    const { date, unassigned = [], unassigned_count: count = 0, unpublished_rosters: rosters = [] } = report;
    const shown = unassigned.slice(0, NAMES_SHOWN);
    const remaining = count - shown.length;

    return (
        <div className="mx-3 mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-900/20">
            <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                        Shift assignment pending for {dayjs(date).format("DD MMM YYYY")}
                    </p>

                    {count > 0 && (
                        <div className="mt-1.5">
                            <p className="flex items-center gap-1 text-xs text-amber-800 dark:text-amber-300">
                                <CalendarX2 className="h-3.5 w-3.5" />
                                {count === 1
                                    ? "1 employee had no shift assigned. That day is marked absent."
                                    : `${count} employees had no shift assigned. Those days are marked absent.`}
                            </p>
                            <div className="mt-1 flex flex-wrap gap-1">
                                {shown.map((row) => (
                                    <span
                                        key={row.employee_id}
                                        title={row.department || undefined}
                                        className="inline-flex rounded-full border border-amber-300 bg-white px-2 py-0.5 text-[11px] font-medium text-amber-900 dark:border-amber-700 dark:bg-gray-900 dark:text-amber-200"
                                    >
                                        {row.employee_name}
                                        {row.employee_code ? ` · ${row.employee_code}` : ""}
                                    </span>
                                ))}
                                {remaining > 0 && (
                                    <span className="inline-flex items-center px-1 text-[11px] text-amber-800 dark:text-amber-300">
                                        and {remaining} more
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {rosters.length > 0 && (
                        <p className="mt-1.5 flex items-center gap-1 text-xs text-amber-800 dark:text-amber-300">
                            <FileWarning className="h-3.5 w-3.5" />
                            {rosters.length === 1
                                ? "1 roster covering that date was never published, so it did not take effect."
                                : `${rosters.length} rosters covering that date were never published, so they did not take effect.`}
                        </p>
                    )}

                    <div className="mt-2 flex items-center gap-2">
                        <Button
                            size="sm"
                            className="gap-1 bg-amber-600 text-white hover:bg-amber-700"
                            onClick={() => navigate("/roster/duty-roster", {
                                state: { from_date: date, to_date: date },
                            })}
                        >
                            Open duty roster <ArrowRight className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDismissed(true)}
                            className="text-amber-800 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/40">
                            Dismiss
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PendingShiftAlert;
